'use client';

import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { parseNumberInput, formatDisplayValue } from './utils';

export const useRange = (
  value: number[],
  minValue: number,
  maxValue: number,
  setValue: (value: number[]) => void,
) => {
  const [innerValue, setInnerValue] = useState<[number, number]>([minValue, maxValue]);
  const [inputValue, setInputValue] = useState<[string, string]>([
    String(minValue),
    String(maxValue),
  ]);
  const [isFocused, setIsFocused] = useState<[boolean, boolean]>([false, false]);
  const isFocusedRef = useRef<[boolean, boolean]>([false, false]);
  const lastCommittedRef = useRef<[number, number]>([minValue, maxValue]);

  const debouncedUpdate = useRef(
    debounce((corrected: [number, number]) => {
      setValue(corrected);
      lastCommittedRef.current = corrected;
    }, 600),
  ).current;

  useEffect(() => {
    const initialValue = value?.length === 2 ? value : [minValue, maxValue];
    setInnerValue([Math.min(initialValue[0], maxValue), Math.min(initialValue[1], maxValue)]);
    setInputValue((prev) => [
      isFocusedRef.current[0] ? prev[0] : formatDisplayValue(initialValue[0], maxValue, false),
      isFocusedRef.current[1]
        ? prev[1]
        : formatDisplayValue(initialValue[1], maxValue, false, true),
    ]);
    lastCommittedRef.current = [initialValue[0], initialValue[1]];
  }, [value, minValue, maxValue]);

  const handleFocus = (index: 0 | 1) => () => {
    const newFocus: [boolean, boolean] = [isFocused[0], isFocused[1]];
    newFocus[index] = true;
    setIsFocused(newFocus);
    isFocusedRef.current = newFocus;

    setInputValue((old) => {
      const newInput: [string, string] = [old[0], old[1]];
      newInput[index] = String(parseNumberInput(newInput[index]));
      return newInput;
    });
  };

  const handleBlur = (index: 0 | 1) => () => {
    const newFocus: [boolean, boolean] = [isFocused[0], isFocused[1]];
    newFocus[index] = false;
    setIsFocused(newFocus);
    isFocusedRef.current = newFocus;

    debouncedUpdate.cancel();

    const raw = inputValue[index];
    const fallback = index === 0 ? minValue : maxValue;
    const parsed = raw === '' ? fallback : parseNumberInput(raw);

    // Both fields: clamp to [minValue, maxValue]
    const clamped = Math.max(minValue, Math.min(maxValue, parsed));

    const newInput: [string, string] = [inputValue[0], inputValue[1]];
    newInput[index] = formatDisplayValue(clamped, maxValue, false, index === 1);

    const v0 = parseNumberInput(newInput[0]);
    const v1 = parseNumberInput(newInput[1]);

    const clampedV0 = Math.max(minValue, Math.min(maxValue, v0));
    const clampedV1 = Math.max(minValue, Math.min(maxValue, v1));

    let corrected: [number, number] = [clampedV0, clampedV1];

    // If min > max: set max = min (shows as "min+", API sends null for max)
    if (corrected[0] > corrected[1]) {
      corrected = [corrected[0], corrected[0]];
      newInput[1] = formatDisplayValue(corrected[0], maxValue, false, true);
    }

    setInputValue(newInput);
    setInnerValue([Math.min(corrected[0], maxValue), Math.min(corrected[1], maxValue)]);

    if (
      corrected[0] !== lastCommittedRef.current[0] ||
      corrected[1] !== lastCommittedRef.current[1]
    ) {
      setValue(corrected);
    }
    lastCommittedRef.current = corrected;
  };

  const handleInputChange = (index: 0 | 1) => (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const raw = e.target.value.replace(/[^\d]/g, '');
    const updatedInput: [string, string] = [inputValue[0], inputValue[1]];
    updatedInput[index] = raw;
    setInputValue(updatedInput);

    if (raw === '') return;

    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) return;

    // Both fields: clamped to [minValue, maxValue]
    const clamped = Math.max(minValue, Math.min(maxValue, parsed));

    const otherIndex = index === 0 ? 1 : 0;
    const otherClamped = Math.max(
      minValue,
      Math.min(maxValue, parseNumberInput(updatedInput[otherIndex])),
    );

    let corrected: [number, number] =
      index === 0 ? [clamped, otherClamped] : [otherClamped, clamped];

    // If min > max while typing: set max = min (shows as "min+")
    if (corrected[0] > corrected[1]) {
      corrected = [corrected[0], corrected[0]];
    }

    setInnerValue([Math.min(corrected[0], maxValue), Math.min(corrected[1], maxValue)]);
    debouncedUpdate(corrected);
  };

  const handleSliderChange = (e: React.SyntheticEvent | Event, newValue: number | number[]) => {
    e.stopPropagation();
    const range = newValue as [number, number];
    setInnerValue(range);
    setInputValue([String(range[0]), String(range[1])]);
  };

  const handleSliderCommit = (e: React.SyntheticEvent | Event, newValue: number | number[]) => {
    e.stopPropagation();
    const range = newValue as [number, number];
    setValue(range);
    lastCommittedRef.current = range;
  };

  return {
    innerValue,
    inputValue,
    handleFocus,
    handleBlur,
    handleInputChange,
    handleSliderChange,
    handleSliderCommit,
  };
};
